import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import {
  Mail, Send, Upload, Image, Paperclip, Users, FileText,
  X, Check, AlertCircle, Plus, Trash2, Eye, ChevronDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CampaignComposer = ({ leads = [], onCampaignSent }) => {
  // State
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [importedRecipients, setImportedRecipients] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [inlineImages, setInlineImages] = useState([]);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [recipientSource, setRecipientSource] = useState('leads'); // 'leads' or 'import' or 'both'
  const [selectAll, setSelectAll] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const contentRef = useRef(null);

  // Handle file upload (attachment)
  const handleAttachmentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('File too large (max 10MB)');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', 'attachment');

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/campaigns/upload-file`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setAttachments(prev => [...prev, response.data]);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    }
  };

  // Handle inline image upload
  const handleInlineImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Image too large (max 5MB)');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', 'inline');

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/campaigns/upload-file`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      // Add to inline images list
      setInlineImages(prev => [...prev, response.data]);
      
      // Insert placeholder into content
      const placeholder = `{{inline:${response.data.file_id}}}`;
      const imageHtml = `<div style="text-align: center; margin: 20px 0;"><img src="${placeholder}" alt="${response.data.filename}" style="max-width: 100%; border-radius: 8px;" /></div>`;
      setHtmlContent(prev => prev + imageHtml);
      
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload image');
    }
  };

  // Handle CSV import
  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/campaigns/import-csv`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setImportedRecipients(response.data.recipients || []);
      setImportResult(response.data);
      setRecipientSource('import');
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to import CSV');
    }
  };

  // Remove attachment
  const removeAttachment = (fileId) => {
    setAttachments(prev => prev.filter(a => a.file_id !== fileId));
  };

  // Remove inline image
  const removeInlineImage = (fileId) => {
    setInlineImages(prev => prev.filter(i => i.file_id !== fileId));
    setHtmlContent(prev => prev.replace(new RegExp(`<div[^>]*>\\s*<img[^>]*src="\\{\\{inline:${fileId}\\}\\}"[^>]*/>\\s*</div>`, 'g'), ''));
  };

  // Toggle lead selection
  const toggleLeadSelection = (lead) => {
    setSelectedRecipients(prev => {
      const exists = prev.find(r => r.email === lead.email);
      if (exists) {
        return prev.filter(r => r.email !== lead.email);
      }
      return [...prev, { email: lead.email, name: lead.name }];
    });
  };

  // Select all leads
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(leads.map(l => ({ email: l.email, name: l.name })));
    }
    setSelectAll(!selectAll);
  };

  // Get all recipients
  const getAllRecipients = useCallback(() => {
    const recipients = [];
    const seen = new Set();

    if (recipientSource === 'leads' || recipientSource === 'both') {
      selectedRecipients.forEach(r => {
        if (!seen.has(r.email)) {
          seen.add(r.email);
          recipients.push(r);
        }
      });
    }

    if (recipientSource === 'import' || recipientSource === 'both') {
      importedRecipients.forEach(r => {
        if (!seen.has(r.email)) {
          seen.add(r.email);
          recipients.push(r);
        }
      });
    }

    return recipients;
  }, [recipientSource, selectedRecipients, importedRecipients]);

  // Send campaign
  const sendCampaign = async () => {
    const recipients = getAllRecipients();

    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!htmlContent.trim()) {
      setError('Please enter email content');
      return;
    }
    if (recipients.length === 0) {
      setError('Please select at least one recipient');
      return;
    }

    setSending(true);
    setError('');

    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('html_content', htmlContent);
    formData.append('recipients', JSON.stringify(recipients));
    formData.append('attachment_ids', JSON.stringify(attachments.map(a => a.file_id)));
    formData.append('inline_image_ids', JSON.stringify(inlineImages.map(i => i.file_id)));

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/campaigns/send`,
        formData,
        { withCredentials: true }
      );

      setSuccess(`Campaign sent successfully! ${response.data.sent_count} emails sent.`);
      
      // Reset form
      setSubject('');
      setHtmlContent('');
      setSelectedRecipients([]);
      setImportedRecipients([]);
      setAttachments([]);
      setInlineImages([]);
      setSelectAll(false);
      setImportResult(null);

      if (onCampaignSent) onCampaignSent(response.data);

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const recipientCount = getAllRecipients().length;

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-3 rounded-lg">
          <Check className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{success}</p>
          <button onClick={() => setSuccess('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Compose */}
        <div className="lg:col-span-2 space-y-4">
          {/* Subject */}
          <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4">
            <label className="block text-sm text-gray-400 mb-2">Subject Line</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Property Update: William Lane Estate"
              className="w-full bg-[#0a0d14] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#d4af37]"
              data-testid="campaign-subject"
            />
          </div>

          {/* Content Editor */}
          <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-gray-400">Email Content</label>
              <div className="flex gap-2">
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#d4af37]/10 text-[#d4af37] rounded-lg text-xs hover:bg-[#d4af37]/20 transition-colors"
                  data-testid="insert-image-btn"
                >
                  <Image className="w-3.5 h-3.5" />
                  Insert Image
                </button>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-gray-300 rounded-lg text-xs hover:bg-gray-700 transition-colors"
                >
                  <Eye className="w-3.5 h-3.5" />
                  {showPreview ? 'Edit' : 'Preview'}
                </button>
              </div>
            </div>

            <input
              type="file"
              ref={imageInputRef}
              onChange={handleInlineImageUpload}
              accept="image/*"
              className="hidden"
            />

            {showPreview ? (
              <div 
                className="bg-[#1a1f2e] rounded-lg p-6 min-h-[300px] prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent || '<p class="text-gray-500">No content yet...</p>') }}
              />
            ) : (
              <textarea
                ref={contentRef}
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                placeholder="<p>Dear {{name}},</p>

<p>We're excited to share the latest updates about the William Lane Estate...</p>

<p>Best regards,<br>William Lane Estate</p>"
                className="w-full bg-[#0a0d14] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#d4af37] min-h-[300px] font-mono text-sm resize-none"
                data-testid="campaign-content"
              />
            )}

            <p className="text-xs text-gray-500 mt-2">
              Use <code className="bg-gray-800 px-1 rounded">{'{{name}}'}</code> for recipient's name and <code className="bg-gray-800 px-1 rounded">{'{{email}}'}</code> for their email.
            </p>
          </div>

          {/* Inline Images */}
          {inlineImages.length > 0 && (
            <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4">
              <label className="block text-sm text-gray-400 mb-3">Inline Images</label>
              <div className="flex flex-wrap gap-2">
                {inlineImages.map(img => (
                  <div key={img.file_id} className="flex items-center gap-2 bg-[#0a0d14] px-3 py-2 rounded-lg">
                    <Image className="w-4 h-4 text-[#d4af37]" />
                    <span className="text-sm text-gray-300 truncate max-w-[150px]">{img.filename}</span>
                    <button onClick={() => removeInlineImage(img.file_id)} className="text-gray-500 hover:text-red-400">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attachments */}
          <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-gray-400">Attachments</label>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-gray-300 rounded-lg text-xs hover:bg-gray-700 transition-colors"
                data-testid="attach-file-btn"
              >
                <Paperclip className="w-3.5 h-3.5" />
                Add File
              </button>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAttachmentUpload}
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
              className="hidden"
            />

            {attachments.length === 0 ? (
              <p className="text-sm text-gray-500">No attachments. Add PDFs or images up to 10MB.</p>
            ) : (
              <div className="space-y-2">
                {attachments.map(att => (
                  <div key={att.file_id} className="flex items-center justify-between bg-[#0a0d14] px-3 py-2 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#d4af37]" />
                      <span className="text-sm text-gray-300">{att.filename}</span>
                      <span className="text-xs text-gray-500">({(att.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <button onClick={() => removeAttachment(att.file_id)} className="text-gray-500 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Recipients */}
        <div className="space-y-4">
          {/* Recipient Source Toggle */}
          <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4">
            <label className="block text-sm text-gray-400 mb-3">Recipients Source</label>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setRecipientSource('leads')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  recipientSource === 'leads' ? 'bg-[#d4af37] text-[#0a0d14]' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <Users className="w-4 h-4" />
                From Leads ({leads.length})
              </button>
              <button
                onClick={() => csvInputRef.current?.click()}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                data-testid="import-csv-btn"
              >
                <Upload className="w-4 h-4" />
                Import CSV
              </button>
              {importedRecipients.length > 0 && (
                <button
                  onClick={() => setRecipientSource('both')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    recipientSource === 'both' ? 'bg-[#d4af37] text-[#0a0d14]' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  Both Sources
                </button>
              )}
            </div>

            <input
              type="file"
              ref={csvInputRef}
              onChange={handleCsvImport}
              accept=".csv"
              className="hidden"
            />
          </div>

          {/* Import Result */}
          {importResult && (
            <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Check className="w-4 h-4 text-green-400" />
                <span className="text-sm text-white">CSV Imported</span>
              </div>
              <div className="text-xs text-gray-400 space-y-1">
                <p>{importResult.imported_count} emails imported</p>
                {importResult.duplicate_count > 0 && (
                  <p>{importResult.duplicate_count} duplicates removed</p>
                )}
                {importResult.error_count > 0 && (
                  <p className="text-red-400">{importResult.error_count} errors</p>
                )}
              </div>
            </div>
          )}

          {/* Lead Selection */}
          {(recipientSource === 'leads' || recipientSource === 'both') && (
            <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-gray-400">Select Leads</label>
                <button
                  onClick={handleSelectAll}
                  className="text-xs text-[#d4af37] hover:underline"
                >
                  {selectAll ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="max-h-[300px] overflow-y-auto space-y-1">
                {leads.map(lead => (
                  <label
                    key={lead.id || lead.email}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#0a0d14]/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRecipients.some(r => r.email === lead.email)}
                      onChange={() => toggleLeadSelection(lead)}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-[#d4af37] focus:ring-[#d4af37]"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{lead.name}</p>
                      <p className="text-xs text-gray-500 truncate">{lead.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Imported Recipients Preview */}
          {(recipientSource === 'import' || recipientSource === 'both') && importedRecipients.length > 0 && (
            <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4">
              <label className="block text-sm text-gray-400 mb-3">
                Imported Recipients ({importedRecipients.length})
              </label>
              <div className="max-h-[200px] overflow-y-auto space-y-1">
                {importedRecipients.slice(0, 20).map((r) => (
                  <div key={r.email} className="flex items-center gap-2 p-2 rounded-lg bg-[#0a0d14]/50">
                    <Mail className="w-3 h-3 text-gray-500" />
                    <span className="text-xs text-gray-300 truncate">{r.name} &lt;{r.email}&gt;</span>
                  </div>
                ))}
                {importedRecipients.length > 20 && (
                  <p className="text-xs text-gray-500 text-center py-2">
                    +{importedRecipients.length - 20} more
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Send Button */}
          <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4">
            <div className="text-center mb-4">
              <p className="text-2xl font-bold text-[#d4af37]">{recipientCount}</p>
              <p className="text-xs text-gray-400">Recipients Selected</p>
            </div>
            <button
              onClick={sendCampaign}
              disabled={sending || recipientCount === 0}
              className="w-full flex items-center justify-center gap-2 bg-[#d4af37] hover:bg-[#c4a030] text-[#0a0d14] font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="send-campaign-btn"
            >
              {sending ? (
                <>
                  <div className="w-5 h-5 border-2 border-[#0a0d14]/30 border-t-[#0a0d14] rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Campaign
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignComposer;
